import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const TemperatureConverter = () => {
  const [temperature, setTemperature] = useState('');
  const [unit, setUnit] = useState('celsius');
  const [convertedTemperature, setConvertedTemperature] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setTemperature(e.target.value);
    setError('');
  };

  const handleUnitChange = (e) => {
    setUnit(e.target.value);
  };

  const convertTemperature = () => {
    const cleanTemperature = parseFloat(temperature.replace(/\s/g, ''));

    if (isNaN(cleanTemperature)) {
      setError('Please enter a valid number for temperature');
      return;
    }

    let convertedTemp = null;

    if (unit === 'celsius') {
      convertedTemp = {
        fahrenheit: (cleanTemperature * 9/5) + 32,
        kelvin: cleanTemperature + 273.15
      };
    } else if (unit === 'fahrenheit') {
      convertedTemp = {
        celsius: (cleanTemperature - 32) * 5/9,
        kelvin: (cleanTemperature - 32) * 5/9 + 273.15
      };
    } else if (unit === 'kelvin') {
      convertedTemp = {
        celsius: cleanTemperature - 273.15,
        fahrenheit: (cleanTemperature - 273.15) * 9/5 + 32
      };
    }

    setConvertedTemperature(convertedTemp);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-blue-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Temperature Converter | Convert Celsius, Fahrenheit, Kelvin</title>
          <meta name="description" content="Easily convert temperatures between Celsius, Fahrenheit, and Kelvin with this tool." />
          <meta name="keywords" content="temperature converter, celsius, fahrenheit, kelvin" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">🌡️</div>
            <h1 className="text-3xl font-bold text-gray-800">Temperature Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert temperatures between Celsius, Fahrenheit, and Kelvin.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Enter Temperature:</label>
              <input
                type="text"
                value={temperature}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter temperature (e.g., 25)"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Unit:</label>
              <select
                value={unit}
                onChange={handleUnitChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="celsius">Celsius (°C)</option>
                <option value="fahrenheit">Fahrenheit (°F)</option>
                <option value="kelvin">Kelvin (K)</option>
              </select>
            </div>
          </div>

          <button
            onClick={convertTemperature}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {convertedTemperature && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Conversion Results</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="space-y-3">
                  {unit === 'celsius' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fahrenheit</span>
                        <span>{convertedTemperature.fahrenheit.toFixed(2)} °F</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kelvin</span>
                        <span>{convertedTemperature.kelvin.toFixed(2)} K</span>
                      </div>
                    </>
                  )}
                  {unit === 'fahrenheit' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Celsius</span>
                        <span>{convertedTemperature.celsius.toFixed(2)} °C</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Kelvin</span>
                        <span>{convertedTemperature.kelvin.toFixed(2)} K</span>
                      </div>
                    </>
                  )}
                  {unit === 'kelvin' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Celsius</span>
                        <span>{convertedTemperature.celsius.toFixed(2)} °C</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fahrenheit</span>
                        <span>{convertedTemperature.fahrenheit.toFixed(2)} °F</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default TemperatureConverter;